/**
 * Helper utilities for job parameter building
 * 
 * These functions provide common utilities for working with
 * job parameters, including value checking, priority selection,
 * and CSV conversion
 */

/**
 * Check if a value is non-empty
 * 
 * @param value - Value to check
 * @returns true if value is non-empty, false otherwise
 * 
 * @example
 * hasValue([1, 2, 3]) // true
 * hasValue([]) // false
 * hasValue("hello") // true
 * hasValue("") // false
 * hasValue(0) // true
 * hasValue(null) // false
 * hasValue(undefined) // false
 */
export function hasValue(value: unknown): boolean {
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "string") return value.trim().length > 0;
  if (typeof value === "number") return !Number.isNaN(value);
  return value !== undefined && value !== null;
}

/**
 * Pick first non-empty value (priority helper)
 * 
 * Used to implement priority rules: User Filters > Creator Preferences > Defaults
 * 
 * @param primary - Primary value (highest priority)
 * @param fallback - Fallback value (lower priority)
 * @returns Primary value if non-empty, otherwise fallback
 * 
 * @example
 * pickValue("user-filter", "creator-pref") // "user-filter"
 * pickValue(undefined, "creator-pref") // "creator-pref"
 * pickValue([], ["default"]) // ["default"]
 * pickValue(["user"], ["creator"]) // ["user"]
 */
export function pickValue<T>(primary?: T, fallback?: T): T | undefined {
  return hasValue(primary) ? primary : fallback;
}

/**
 * Merge two values, combining arrays via union (deduplicated)
 *
 * Used for public/candidate-side param building where creator defaults
 * should always be preserved and candidate filters add on top.
 *
 * For arrays: returns deduplicated union of both
 * For scalars: primary wins if non-empty, else fallback
 *
 * @param primary - User-applied value (candidate filters)
 * @param fallback - Creator default value
 * @returns Merged value
 *
 * @example
 * mergeValue(["design"], ["software-development"]) // ["software-development", "design"]
 * mergeValue([], ["software-development"]) // ["software-development"]
 * mergeValue(100000, 50000) // 100000
 * mergeValue(undefined, 50000) // 50000
 */
export function mergeValue<T>(primary?: T, fallback?: T): T | undefined {
  if (Array.isArray(primary) && Array.isArray(fallback)) {
    const merged = [...new Set([...fallback, ...primary])].filter(Boolean);
    return (merged.length > 0 ? merged : undefined) as T | undefined;
  }
  return hasValue(primary) ? primary : fallback;
}

/**
 * Convert array or CSV string to normalized CSV string
 * 
 * Applies normalization function if provided. Handles both array and string inputs.
 * Filters out empty values and trims whitespace.
 * 
 * @param value - Array of strings or CSV string
 * @param normalize - Optional normalization function to apply to each entry
 * @returns Normalized CSV string, or undefined if input is empty
 * 
 * @example
 * toCsv(["React", "TypeScript"]) // "React,TypeScript"
 * toCsv("React, TypeScript") // "React,TypeScript"
 * toCsv(["US", "CA"], normalizeCountryCode) // "US,CA"
 * toCsv("remote, hybrid", normalizeWorkMode) // "remote,hybrid"
 * toCsv([]) // undefined
 * toCsv("") // undefined
 */
export function toCsv(
  value?: string | string[],
  normalize?: (entry: string) => string
): string | undefined {
  if (!hasValue(value)) return undefined;

  // Handle array input
  if (Array.isArray(value)) {
    const cleaned = value.map((entry) => entry.trim()).filter(Boolean);
    const normalized = normalize ? cleaned.map((entry) => normalize(entry)) : cleaned;
    return normalized.length > 0 ? normalized.join(",") : undefined;
  }

  // Handle string input
  if (typeof value !== "string") return undefined;
  
  const entries = value.split(",").map((entry) => entry.trim()).filter(Boolean);
  const normalized = normalize ? entries.map((entry) => normalize(entry)) : entries;
  const cleaned = normalized.filter(Boolean);
  
  return cleaned.length > 0 ? cleaned.join(",") : undefined;
}
